import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Button, Select, Input, message } from "antd";
import {
  SendOutlined,
  WhatsAppOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { PageSizeType, TemplatesTypes } from "../templates";
import { useIsMobile, useIsTablet } from "../../Hook";
import { INVOICE_PAGE_SIZES, INVOICE_TEMPLATES } from "../../Constant";
import {
  useSendWhatsAppDocumentMutation,
  useSendWhatsAppTextMessageMutation,
  useSendSMSMutation,
} from "../../Services";
import { useReturnInvoiceData } from "./useReturnInvoiceData";
import { useReturnInvoiceDocument } from "./useReturnInvoiceDocument";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";

interface ReturnInvoiceDrawerProps {
  open: boolean;
  returnId: string | null;
  returnNo?: string;
  creditOnly?: boolean;
  onClose: () => void;
}

export const ReturnInvoiceDrawer: React.FC<ReturnInvoiceDrawerProps> = ({
  open,
  returnId,
  returnNo,
  creditOnly = false,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [pageSize, setPageSize] = useState<PageSizeType>("A4");
  const [template, setTemplate] = useState<TemplatesTypes>(
    TemplatesTypes.Classic,
  );
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);

  const { orderReturn, config } = useReturnInvoiceData({ open, returnId });
  const { getReturnHtml, printInvoice, downloadInvoice, getReturnPdfBase64 } =
    useReturnInvoiceDocument({
      orderReturn,
      config,
      pageSize,
      template,
      creditOnly,
    });

  const documentLabel = creditOnly ? "Customer Credit" : "Return Invoice";

  const [whatsappPhoneState, setWhatsappPhoneState] = useState<string>("");

  const whatsappPhone = whatsappPhoneState.trim()
    ? whatsappPhoneState
    : String(orderReturn?.customer_phone || config?.whatsapp || "");

  const sendDocumentInvoiceMutation = useSendWhatsAppDocumentMutation();
  const sendWhatsAppTextMutation = useSendWhatsAppTextMessageMutation();
  const sendSmsMutation = useSendSMSMutation();
  const { isWhatsAppEnabled, isSmsGatewayEnable } = useSystemConfig();
  const isWhatsAppConnected = true;

  const handleSendWhatsApp = useCallback(async () => {
    if (!orderReturn || !whatsappPhone.trim()) {
      message.warning(
        "Enter the WhatsApp phone number to send the return invoice to.",
      );
      return;
    }

    try {
      const pdfBase64 = await getReturnPdfBase64();
      if (!pdfBase64) {
        message.error("Failed to generate the return invoice PDF.");
        return;
      }

      const res = await sendDocumentInvoiceMutation.mutateAsync({
        phone: whatsappPhone.trim(),
        document_base64: pdfBase64,
        filename: `${documentLabel.replace(/\s/g, "")}_${String(orderReturn.return_no || "return")}.pdf`,
        caption: `${documentLabel} ${orderReturn.return_no} - ${Number(
          creditOnly
            ? orderReturn.exchange_value || 0
            : orderReturn.total_refunded || 0,
        ).toFixed(
          2,
        )} ${config.currency || "LKR"}. Thank you for shopping with us!`,
      });

      if (res?.success) {
        message.success(`${documentLabel} sent via WhatsApp!`);
      } else {
        message.error(
          res?.message || `Failed to send the ${documentLabel.toLowerCase()}.`,
        );
      }
    } catch (error: any) {
      message.error(
        error?.message || `Failed to send the ${documentLabel.toLowerCase()}.`,
      );
    }
  }, [
    getReturnPdfBase64,
    orderReturn,
    whatsappPhone,
    sendDocumentInvoiceMutation,
    config,
    creditOnly,
    documentLabel,
  ]);

  const handleSendWhatsAppLink = useCallback(async () => {
    if (!orderReturn || !whatsappPhone.trim()) {
      message.warning(
        "Enter the phone number to send the return invoice link.",
      );
      return;
    }
    try {
      const link = `${window.location.origin}/${localStorage.getItem("tenantId") || ""}/returns/${orderReturn.id}`;
      const textMessage = `Dear Customer, thank you for your business. You can view/download your ${documentLabel.toLowerCase()} #${orderReturn.return_no} here: ${link}`;

      const res = await sendWhatsAppTextMutation.mutateAsync({
        phone: whatsappPhone.trim(),
        message: textMessage,
      });

      if (res?.success) {
        message.success(`${documentLabel} link sent via WhatsApp!`);
      } else {
        message.error(
          res?.message ||
            `Failed to send the ${documentLabel.toLowerCase()} link.`,
        );
      }
    } catch (error: any) {
      message.error(
        error?.message ||
          `Failed to send the ${documentLabel.toLowerCase()} link.`,
      );
    }
  }, [orderReturn, whatsappPhone, sendWhatsAppTextMutation, documentLabel]);

  const handleSendSMSLink = useCallback(async () => {
    if (!orderReturn || !whatsappPhone.trim()) {
      message.warning(
        "Enter the phone number to send the return invoice link.",
      );
      return;
    }
    try {
      const link = `${window.location.origin}/${localStorage.getItem("tenantId") || ""}/returns/${orderReturn.id}`;
      const textMessage = `Dear Customer, thank you for your business. You can view/download your ${documentLabel} #${orderReturn.return_no} here: ${link}`;

      const res = await sendSmsMutation.mutateAsync({
        contact: whatsappPhone.trim(),
        message: textMessage,
      });

      if (res?.success) {
        message.success(`${documentLabel} link sent via SMS!`);
      } else {
        message.error(
          res?.message ||
            `Failed to send the ${documentLabel.toLowerCase()} link.`,
        );
      }
    } catch (error: any) {
      message.error(
        error?.message ||
          `Failed to send the ${documentLabel.toLowerCase()} link.`,
      );
    }
  }, [orderReturn, whatsappPhone, sendSmsMutation, documentLabel]);

  if (!orderReturn) return null;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={drawerTitle(documentLabel + " Preview")}
        width={isMobile || isTablet ? "100%" : DRAWER_FORM_WIDTH}
        className="return-invoice-drawer"
        destroyOnClose
        footer={
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {!creditOnly &&
                orderReturn.refund_type_code === "store_credit" &&
                Number(orderReturn.exchange_value || 0) > 0 && (
                  <Button
                    size="middle"
                    onClick={() => setCreditNoteOpen(true)}
                    id="open_credit_note_pdf"
                    className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
                  >
                    Customer Credit
                  </Button>
                )}
              <Button
                type="primary"
                size="middle"
                onClick={printInvoice}
                id={
                  creditOnly
                    ? "print_credit_note_pdf"
                    : "print_return_invoice_pdf"
                }
                className="flex-grow min-w-[100px] text-xs font-semibold h-9 rounded-md"
              >
                Print {documentLabel}
              </Button>
              <Button
                size="middle"
                onClick={downloadInvoice}
                id={
                  creditOnly
                    ? "download_credit_note_pdf"
                    : "download_return_invoice_pdf"
                }
                className="flex-grow min-w-[80px] text-xs font-semibold h-9 rounded-md"
              >
                Download
              </Button>
            </div>
            <div className="flex flex-wrap lg:flex-nowrap gap-2 items-center border-t border-gray-150 pt-2 w-full">
              <Input
                placeholder="WhatsApp number (e.g. 0712345678)"
                prefix={<SendOutlined className="text-gray-400" />}
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhoneState(e.target.value)}
                onPressEnter={handleSendWhatsApp}
                className="h-9 text-xs flex-grow w-full lg:w-auto"
                allowClear
              />
              <Button
                type="primary"
                size="middle"
                loading={sendDocumentInvoiceMutation.isPending}
                onClick={handleSendWhatsApp}
                id={
                  creditOnly
                    ? "send_credit_note_whatsapp"
                    : "send_return_invoice_whatsapp"
                }
                className="text-xs font-semibold h-9 rounded-md bg-green-600 flex-grow lg:flex-none"
                disabled={!isWhatsAppEnabled || !isWhatsAppConnected}
              >
                Send {creditOnly ? "Note" : "Invoice"}
              </Button>
              <Button
                type="default"
                size="middle"
                loading={sendWhatsAppTextMutation.isPending}
                onClick={handleSendWhatsAppLink}
                id="send_return_link_whatsapp"
                icon={<WhatsAppOutlined className="text-green-500" />}
                className="text-xs font-semibold h-9 rounded-md flex items-center justify-center flex-grow lg:flex-none"
                disabled={!isWhatsAppEnabled || !isWhatsAppConnected}
              >
                Send Link
              </Button>
              <Button
                type="default"
                size="middle"
                loading={sendSmsMutation.isPending}
                onClick={handleSendSMSLink}
                id="send_return_link_sms"
                icon={<MessageOutlined className="text-blue-500" />}
                className="text-xs font-semibold h-9 rounded-md flex items-center justify-center flex-grow lg:flex-none"
                disabled={!isSmsGatewayEnable}
              >
                Send Link
              </Button>
            </div>
            <div className="hidden lg:flex justify-end text-[10px] text-gray-400 px-1 border-t border-gray-150 pt-1.5 mt-0.5">
              <span>Page size cycles in the select below</span>
            </div>
          </div>
        }
      >
        <>
          <div className="flex gap-5 justify-center mb-3">
            <Select
              size="middle"
              className="w-32"
              value={template}
              onChange={(e) => setTemplate(e)}
              options={INVOICE_TEMPLATES}
            />
            <Select
              size="middle"
              className="w-32"
              value={pageSize}
              onChange={(v) => setPageSize(v)}
              options={INVOICE_PAGE_SIZES}
            />
          </div>
          <div
            dangerouslySetInnerHTML={{ __html: getReturnHtml(false) }}
            style={{ width: "100%" }}
          />
        </>
      </Drawer>
      {creditNoteOpen && returnId && (
        <ReturnInvoiceDrawer
          open={creditNoteOpen}
          returnId={returnId}
          returnNo={returnNo}
          creditOnly
          onClose={() => setCreditNoteOpen(false)}
        />
      )}
    </>
  );
};
